import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, ShoppingCart, Users, Truck, Package,
  Factory, Warehouse, Tag, BarChart2, Settings, LogOut,
  ChevronLeft, ChevronRight, ShoppingBag, Menu, ClipboardList, X, CheckCircle, MapPin, FileUp, Calculator, TrendingUp, Scale, Scissors, Receipt, Droplets, FileSearch, BookOpen
} from "lucide-react";
import { cn } from "../lib/utils";

const nav = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/", module: "dashboard" },
  { label: "Ventas", icon: ShoppingCart, path: "/ventas", module: "ventas" },
  { label: "Surtido", icon: ClipboardList, path: "/surtido", module: "surtido" },
  { label: "Compras", icon: ShoppingBag, path: "/compras", module: "compras" },
  { label: "Clientes", icon: Users, path: "/clientes", module: "clientes" },
  { label: "Proveedores", icon: Truck, path: "/proveedores", module: "proveedores" },
  { label: "Productos", icon: Package, path: "/productos", module: "productos" },
  { label: "Rentabilidad", icon: TrendingUp, path: "/rentabilidad", module: "rentabilidad" },
  { label: "Fabricación", icon: Factory, path: "/fabricacion", module: "fabricacion" },
  { label: "Planeación", icon: Calculator, path: "/planeacion", module: "planeacion" },
  { label: "Inventario", icon: Warehouse, path: "/inventario", module: "inventario" },
  { label: "Recepciones", icon: Scale, path: "/recepciones", module: "recepciones" },
  { label: "Transformaciones", icon: Scissors, path: "/transformaciones", module: "transformaciones" },
  { label: "Gastos", icon: Receipt, path: "/gastos", module: "gastos" },
  { label: "Limpieza", icon: Droplets, path: "/limpieza", module: "limpieza" },
  { label: "Trazabilidad", icon: FileSearch, path: "/trazabilidad", module: "trazabilidad" },
  { label: "Guia de Uso", icon: BookOpen, path: "/guia" },
  { label: "Listas de Precios", icon: Tag, path: "/listas-precios", module: "listas_precios" },
  { label: "Envíos", icon: MapPin, path: "/envios", module: "envios" },
  { label: "Importar Ventas", icon: FileUp, path: "/importar-ventas", module: "importar_ventas" },
  { label: "Reportes", icon: BarChart2, path: "/reportes", module: "reportes" },
  { label: "Comprobantes", icon: CheckCircle, path: "/comprobantes", module: "comprobantes" },
  { label: "Configuración", icon: Settings, path: "/configuracion", module: "configuracion" },
];

export default function Layout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const filteredNav = nav.filter(item => hasPermission(item.module));

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = ({ onClose }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-4 border-b border-slate-200",
        collapsed && !onClose && "justify-center px-2"
      )}>
        <img 
          src="https://customer-assets.emergentagent.com/job_pet-treat-factory/artifacts/d23sdlg6_Logo%20Sib.png" 
          alt="Siberian by Rasputín"
          className="w-9 h-9 object-contain flex-shrink-0"
        />
        {(!collapsed || onClose) && (
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-slate-900 leading-tight truncate">Siberian</div>
            <div className="text-xs text-slate-500">by Rasputín</div>
          </div>
        )}
        {onClose && (
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {filteredNav.map((item) => {
          const active = location.pathname === item.path ||
            (item.path !== "/" && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={cn(
                "flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg text-sm font-medium transition-colors mb-0.5",
                active
                  ? "bg-brand-50 text-brand-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                collapsed && !onClose && "justify-center px-2"
              )}
            >
              <item.icon size={18} className={cn("flex-shrink-0", active ? "text-brand-600" : "text-slate-500")} />
              {(!collapsed || onClose) && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User */}
      <div className={cn("border-t border-slate-200 p-3", collapsed && !onClose && "px-2")}>
        {(!collapsed || onClose) && (
          <div className="mb-2 px-1">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className={cn(
            "flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors w-full px-1 py-1 rounded-md hover:bg-red-50",
            collapsed && !onClose && "justify-center"
          )}
        >
          <LogOut size={16} />
          {(!collapsed || onClose) && <span>Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col bg-white border-r border-slate-200 transition-all duration-200 relative flex-shrink-0",
        collapsed ? "w-16" : "w-56"
      )}>
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 bg-white border border-slate-200 rounded-full p-1 shadow-sm hover:bg-slate-50 transition-colors z-10"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-2xl">
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 px-3 md:px-6 py-3 flex items-center gap-3 flex-shrink-0">
          <button
            className="md:hidden p-2 rounded-md hover:bg-slate-100 text-slate-600 flex-shrink-0"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu size={20} />
          </button>
          {/* Page title from nav */}
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-slate-700 truncate hidden sm:block">
              {nav.find(n => n.path === location.pathname || (n.path !== "/" && location.pathname.startsWith(n.path)))?.label || ""}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:inline text-sm text-slate-500 truncate max-w-32">{user?.name}</span>
            <span className="inline-flex items-center bg-brand-50 text-brand-700 text-xs font-medium px-2 py-0.5 rounded-full capitalize">
              {user?.role}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
